import React, { useId, useMemo, useRef, useState } from "react";
import { Badge, Checkbox } from "../../ForBrands/ui/BrandUI";
import {
  COLUMN,
  COLUMNS,
  EMPTY,
  MAX_JERSEY,
  POSITIONS,
  STAT_KEYS,
  columnTitle,
  derivedText,
  isBlockPaste,
  isCovered,
  isLocked,
  lineTotals,
  pasteIntoRows,
  pasteSummaryText,
  pctText,
  plural,
  setRowCell,
  setRowDnp,
} from "./gameStatsModel";

// The game sheet: a compact spreadsheet in the V2 style, one row per roster
// player and a totals row, in the columns of Jayson's sheet. Every cell is
// an always-on field in a 32px box (the CacheShare amount box: bm-border,
// orange with the 3px ring on focus); a click or arrival selects what is in
// it, so typing replaces it.
//
// Keyboard: the arrows move between cells (left alone with Alt, Ctrl, Cmd
// or Shift held), Enter moves down and Shift+Enter up, Tab stays the
// browser's, Home and End go to the row's first and last stat, Escape puts
// back what the cell held when it took focus (and never reaches a modal
// around the sheet), Delete and Backspace clear a cell nothing was typed in
// yet. A paste of more than one cell fills the sheet as the model reads it
// and says what it filled and which names matched nobody.
//
// FG%, 3P% and FT% are worked out, never typed. A line Cerebro already has
// is read-only with an "On Cerebro" badge (it is not counted twice); a saved
// line of a player who has left the roster since stays editable with a "Not
// on roster" badge; `readOnly` shows the whole sheet as text. `errors` and `warnings` are
// {player_id: {key: message}}: a cell with an error gets the critical
// border, aria-invalid and its message (as a tooltip and in the list under
// the sheet).

const cx = (...parts) => parts.filter(Boolean).join(" ");

const DNP_COLUMN = { key: "dnp", label: "DNP", name: "did not play", kind: "dnp" };
const DISPLAY = [COLUMN.player, DNP_COLUMN, ...COLUMNS.slice(1)];
const BODY_COLUMNS = COLUMNS.slice(3);

// Column widths (px). The player column takes whatever is left, at least
// 200, so the sheet fits 1392px (a full-bleed admin page) without a scroll.
const WIDTH = { dnp: 44, jersey: 52, position: 56, stat: 52, derived: 56 };
const PLAYER_MIN = 200;
const widthOf = (col) => (col.kind === "player" ? null : WIDTH[col.key] || WIDTH[col.kind]);
export const GRID_MIN_WIDTH = PLAYER_MIN + DISPLAY.reduce((sum, col) => sum + (widthOf(col) || 0), 0);

// The cells the keyboard walks, left to right (the worked-out columns are
// stepped over).
const NAV_KEYS = ["dnp", "jersey", "position", ...STAT_KEYS];
const FIRST_STAT = STAT_KEYS[0];
const LAST_STAT = STAT_KEYS[STAT_KEYS.length - 1];
const STEP = { ArrowLeft: [0, -1], ArrowRight: [0, 1], ArrowUp: [-1, 0], ArrowDown: [1, 0] };
const KEY_ORDER = Object.fromEntries(["player_id", ...NAV_KEYS].map((k, i) => [k, i]));

export const PASTE_HINT = "Paste a box score from Excel or Google Sheets into the first cell.";

// The box a field sits in (class names stay whole so Tailwind finds them).
const BOX = "flex h-8 w-full min-w-0 cursor-text items-center rounded-[6px] border px-1 transition-colors";
const BOX_IDLE =
  "border-bm-border bg-white hover:border-bm-strong focus-within:border-bm-orange focus-within:shadow-[0_0_0_3px_rgba(255,68,0,0.12)]";
const BOX_INVALID = "border-bm-critical bg-white focus-within:shadow-[0_0_0_3px_rgba(226,36,26,0.12)]";
const BOX_WARN =
  "border-bm-attention bg-white focus-within:border-bm-orange focus-within:shadow-[0_0_0_3px_rgba(255,68,0,0.12)]";
const BOX_OFF = "cursor-not-allowed border-transparent bg-bm-subtle";
const FIELD =
  "bm-bare h-full w-full min-w-0 bg-transparent text-center bm-body-m tabular-nums text-bm-ink outline-none disabled:cursor-not-allowed disabled:text-bm-tertiary";
const SELECT =
  "bm-input bm-select !h-8 !rounded-[6px] !pl-2 !pr-5 ![background-position:right_6px_center] tabular-nums";
const SELECT_INVALID = "!border-bm-critical";
const TD = "border-b border-bm-border px-[3px] py-1 align-middle";
const TEXT = "block text-center bm-body-m tabular-nums";

const totalText = (key, value) => (key === "min" ? String(Math.round(value * 10) / 10) : String(value));

// One always-on field. The label is the box, so a click anywhere in it
// lands in the field.
const CellInput = ({ value, onValue, label, message, invalid, warning, describedBy, disabled, decimal, maxLength }) => (
  <label className={cx(BOX, disabled ? BOX_OFF : invalid ? BOX_INVALID : warning ? BOX_WARN : BOX_IDLE)}>
    <input
      value={value}
      onChange={(e) => onValue(e.target.value)}
      aria-label={label}
      aria-invalid={invalid ? "true" : undefined}
      aria-describedby={describedBy}
      title={message || undefined}
      inputMode={decimal ? "decimal" : "numeric"}
      autoComplete="off"
      spellCheck={false}
      disabled={disabled}
      maxLength={maxLength}
      className={FIELD}
    />
  </label>
);

const GameStatsGrid = ({
  rows = [],
  onChange,
  errors = {},
  warnings = {},
  readOnly = false,
  label = "Box score",
  hint = PASTE_HINT,
  className = "",
}) => {
  const gridId = useId();
  const tableRef = useRef(null);
  // The value the focused cell held when it took focus (what Escape puts
  // back), and whether anything was typed in it since.
  const opened = useRef({ row: -1, key: "", value: "" });
  const typed = useRef(false);
  // A click that focused a field keeps the selection the focus made.
  const keepSelection = useRef(false);
  const [pasteNote, setPasteNote] = useState("");

  const emit = (next, change) => onChange?.(next, change);
  const changeCell = (r, key, value) => emit(setRowCell(rows, r, key, value), { playerId: rows[r]?.playerId, key });
  const changeDnp = (r, on) => emit(setRowDnp(rows, r, on), { playerId: rows[r]?.playerId, key: "dnp" });

  const cellOf = (target) => {
    const td = target?.closest?.("[data-col]");
    if (!td || !tableRef.current?.contains(td)) return null;
    return { row: Number(td.dataset.row), key: td.dataset.col };
  };

  const controlAt = (r, key) => {
    const td = tableRef.current?.querySelector(`[data-row="${r}"][data-col="${key}"]`);
    const control = td?.querySelector("input, select, button");
    return control && !control.disabled ? control : null;
  };

  const focusControl = (control) => {
    if (!control) return false;
    control.focus();
    if (control.tagName === "INPUT") control.select();
    return true;
  };

  const move = (r, key, dRow, dCol) => {
    if (dCol) {
      for (let i = NAV_KEYS.indexOf(key) + dCol; i >= 0 && i < NAV_KEYS.length; i += dCol) {
        if (focusControl(controlAt(r, NAV_KEYS[i]))) return;
      }
      return;
    }
    for (let next = r + dRow; next >= 0 && next < rows.length; next += dRow) {
      if (focusControl(controlAt(next, key))) return;
    }
  };

  const onFocus = (e) => {
    const cell = cellOf(e.target);
    const tag = e.target?.tagName;
    if (!cell || (tag !== "INPUT" && tag !== "SELECT")) return;
    opened.current = { row: cell.row, key: cell.key, value: e.target.value };
    typed.current = false;
    if (tag === "INPUT") {
      e.target.select();
      keepSelection.current = true;
    }
  };

  const onMouseUp = (e) => {
    if (keepSelection.current && e.target?.tagName === "INPUT") e.preventDefault();
    keepSelection.current = false;
  };

  const onKeyDown = (e) => {
    if (readOnly || e.nativeEvent?.isComposing) return;
    const cell = cellOf(e.target);
    if (!cell) return;
    keepSelection.current = false;
    const { row, key } = cell;
    const target = e.target;
    const field = target.tagName === "INPUT" || target.tagName === "SELECT";
    const mod = e.altKey || e.ctrlKey || e.metaKey;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowDown": {
        if (mod || e.shiftKey) return;
        e.preventDefault();
        const [dRow, dCol] = STEP[e.key];
        move(row, key, dRow, dCol);
        return;
      }
      case "Enter":
        if (mod) return;
        e.preventDefault();
        move(row, key, e.shiftKey ? -1 : 1, 0);
        return;
      case "Home":
      case "End":
        if (mod || e.shiftKey) return;
        e.preventDefault();
        focusControl(controlAt(row, e.key === "Home" ? FIRST_STAT : LAST_STAT));
        return;
      case "Escape": {
        // Escape in a cell is the sheet's: a modal around it stays open.
        e.stopPropagation();
        if (!field) return;
        e.preventDefault();
        const was = opened.current;
        if (was.row === row && was.key === key && target.value !== was.value) changeCell(row, key, was.value);
        typed.current = false;
        if (target.tagName === "INPUT") {
          setTimeout(() => {
            if (document.activeElement === target) target.select();
          }, 0);
        }
        return;
      }
      case "Delete":
      case "Backspace":
        if (target.tagName !== "INPUT" || typed.current || mod || target.value === "") return;
        e.preventDefault();
        typed.current = true;
        changeCell(row, key, "");
        return;
      default:
    }
  };

  const onPaste = (e) => {
    if (readOnly) return;
    const cell = cellOf(e.target);
    if (!cell) return;
    const data = e.clipboardData;
    const text = (data && (data.getData("text/plain") || data.getData("text"))) || "";
    // One value goes into the focused field the usual way.
    if (!isBlockPaste(text)) return;
    e.preventDefault();
    const { rows: next, summary } = pasteIntoRows(rows, text, { row: cell.row, key: cell.key });
    // Escape after a paste puts back the pasted value, never the one before.
    const pasted = next[cell.row];
    if (pasted && cell.key !== "dnp") {
      const value = cell.key === "jersey" || cell.key === "position" ? pasted[cell.key] : pasted.cells?.[cell.key];
      opened.current = { row: cell.row, key: cell.key, value: value ?? "" };
    }
    emit(next, { paste: true });
    setPasteNote(pasteSummaryText(summary));
  };

  const { totals, players } = useMemo(() => lineTotals(rows), [rows]);
  const messageId = (kind, r, key) => `${gridId}-${kind}-${r}-${key}`;

  // The messages under the sheet, in row and column order.
  const listed = (source, kind) => {
    const out = [];
    rows.forEach((row, r) => {
      const cells = source[row.playerId];
      if (!cells) return;
      Object.keys(cells)
        .sort((a, b) => (KEY_ORDER[a] ?? 99) - (KEY_ORDER[b] ?? 99))
        .forEach((key) => {
          const col = COLUMN[key] || (key === "dnp" ? DNP_COLUMN : null);
          const where = col && col.kind !== "player" ? `${row.name}, ${col.label}` : row.name;
          out.push({ id: messageId(kind, r, key), text: `${where}: ${cells[key]}` });
        });
    });
    const shown = new Set(rows.map((row) => row.playerId));
    Object.entries(source).forEach(([pid, cells]) => {
      if (shown.has(pid)) return;
      Object.values(cells || {}).forEach((msg, i) => out.push({ id: `${gridId}-${kind}-other-${pid}-${i}`, text: `${pid}: ${msg}` }));
    });
    return out;
  };
  const errorList = listed(errors, "error");
  const warningList = listed(warnings, "warning");
  const covered = rows.some(isCovered);

  const cellText = (value) => (value === "" || value === null || value === undefined ? EMPTY : value);

  return (
    <div className={cx("flex min-w-0 flex-col gap-3", className)}>
      {!readOnly && hint && <p className="bm-body-s text-bm-muted">{hint}</p>}
      <div className="overflow-x-auto rounded-[8px] border border-bm-border">
        <table
          ref={tableRef}
          role="grid"
          aria-label={label}
          aria-readonly={readOnly ? "true" : undefined}
          className="w-full table-fixed border-collapse"
          style={{ minWidth: GRID_MIN_WIDTH }}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onMouseUp={onMouseUp}
          onPaste={onPaste}
        >
          <colgroup>
            {DISPLAY.map((col) => (
              <col key={col.key} style={widthOf(col) ? { width: widthOf(col) } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr role="row">
              {DISPLAY.map((col) => (
                <th
                  key={col.key}
                  role="columnheader"
                  scope="col"
                  title={col.kind === "player" ? undefined : columnTitle(col)}
                  className={cx("bm-th !h-9", col.kind === "player" ? "!pl-3 !pr-2" : "!px-1 !text-center")}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => {
              const locked = readOnly || isLocked(row);
              const rowErrors = errors[row.playerId] || {};
              const rowWarnings = warnings[row.playerId] || {};
              const rowMessage = rowErrors.player_id;
              const field = (key, extra = {}) => {
                const message = rowErrors[key] || rowWarnings[key] || "";
                const describedBy = rowErrors[key] ? messageId("error", r, key) : rowWarnings[key] ? messageId("warning", r, key) : undefined;
                return (
                  <CellInput
                    value={key === "jersey" ? row.jersey : row.cells?.[key] ?? ""}
                    onValue={(value) => {
                      typed.current = true;
                      changeCell(r, key, value);
                    }}
                    label={`${row.name}, ${COLUMN[key].name}`}
                    message={message}
                    invalid={Boolean(rowErrors[key])}
                    warning={Boolean(rowWarnings[key])}
                    describedBy={describedBy}
                    {...extra}
                  />
                );
              };
              return (
                <tr key={row.playerId} role="row" className={cx(isLocked(row) && "bg-bm-subtle")}>
                  <th role="rowheader" scope="row" className={cx(TD, "!pl-3 pr-2 text-left font-normal")}>
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={cx("min-w-0 truncate bm-label-l", rowMessage ? "text-bm-critical" : "text-bm-ink")}
                        title={rowMessage || row.name}
                      >
                        {row.name}
                      </span>
                      {isCovered(row) && (
                        <span className="shrink-0" title="Cerebro already has this game for this player, so the line is not counted twice.">
                          <Badge tone="soft">On Cerebro</Badge>
                        </span>
                      )}
                      {row.offRoster && (
                        <span className="shrink-0" title="No longer on the roster. The line stays with this game.">
                          <Badge tone="passive">Not on roster</Badge>
                        </span>
                      )}
                    </span>
                  </th>
                  <td role="gridcell" data-row={r} data-col="dnp" className={cx(TD, "text-center")}>
                    {locked ? (
                      <span className={cx(TEXT, "text-bm-muted")}>{row.dnp ? "DNP" : ""}</span>
                    ) : (
                      <span className="flex justify-center">
                        <Checkbox checked={Boolean(row.dnp)} onChange={(on) => changeDnp(r, on)} aria-label={`${row.name} did not play`} />
                      </span>
                    )}
                  </td>
                  <td role="gridcell" data-row={r} data-col="jersey" className={TD}>
                    {locked ? <span className={cx(TEXT, "text-bm-ink")}>{cellText(row.jersey)}</span> : field("jersey", { maxLength: MAX_JERSEY })}
                  </td>
                  <td role="gridcell" data-row={r} data-col="position" className={TD}>
                    {locked ? (
                      <span className={cx(TEXT, "text-bm-ink")}>{cellText(row.position)}</span>
                    ) : (
                      <select
                        value={row.position || ""}
                        onChange={(e) => changeCell(r, "position", e.target.value)}
                        aria-label={`${row.name}, position`}
                        aria-invalid={rowErrors.position ? "true" : undefined}
                        aria-describedby={rowErrors.position ? messageId("error", r, "position") : undefined}
                        title={rowErrors.position || undefined}
                        className={cx(SELECT, rowErrors.position && SELECT_INVALID)}
                      >
                        <option value="">{EMPTY}</option>
                        {POSITIONS.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                        {row.position && !POSITIONS.includes(row.position) && <option value={row.position}>{row.position}</option>}
                      </select>
                    )}
                  </td>
                  {BODY_COLUMNS.map((col) =>
                    col.kind === "derived" ? (
                      <td key={col.key} role="gridcell" aria-readonly="true" className={TD}>
                        <span className={cx(TEXT, "text-bm-muted")}>{row.dnp ? EMPTY : derivedText(row, col)}</span>
                      </td>
                    ) : (
                      <td key={col.key} role="gridcell" data-row={r} data-col={col.key} className={TD}>
                        {locked ? (
                          <span className={cx(TEXT, "text-bm-ink")}>{row.dnp ? EMPTY : cellText(row.cells?.[col.key])}</span>
                        ) : (
                          field(col.key, { disabled: Boolean(row.dnp), decimal: col.key === "min" })
                        )}
                      </td>
                    ),
                  )}
                </tr>
              );
            })}
            <tr role="row" className="bg-bm-subtle">
              <th role="rowheader" scope="row" className="py-2 pl-3 pr-2 text-left font-normal">
                <span className="bm-label-l text-bm-ink">Team totals</span>
                <span className="ml-2 bm-body-s text-bm-muted">{plural(players, "player")}</span>
              </th>
              <td role="gridcell" />
              <td role="gridcell" />
              <td role="gridcell" />
              {BODY_COLUMNS.map((col) => (
                <td key={col.key} role="gridcell" className="px-[3px] py-2">
                  <span className={cx(TEXT, col.kind === "derived" ? "text-bm-muted" : "bm-label-l text-bm-ink")}>
                    {col.kind === "derived" ? pctText(totals[col.made], totals[col.att]) : totalText(col.key, totals[col.key])}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {pasteNote && (
        <p role="status" aria-live="polite" className="bm-body-s text-bm-n700">
          {pasteNote}
        </p>
      )}
      {covered && (
        <p className="bm-body-s text-bm-muted">
          On Cerebro: Cerebro already has this game for these players, so their lines are shown here but not counted twice.
        </p>
      )}
      {errorList.length > 0 && (
        <ul aria-label="Cells to fix" className="flex flex-col gap-1">
          {errorList.map((item) => (
            <li key={item.id} id={item.id} className="bm-body-s text-bm-critical">
              {item.text}
            </li>
          ))}
        </ul>
      )}
      {warningList.length > 0 && (
        <ul aria-label="Cells to check" className="flex flex-col gap-1">
          {warningList.map((item) => (
            <li key={item.id} id={item.id} className="bm-body-s text-bm-n700">
              {item.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GameStatsGrid;
